import { useEffect, useState, useCallback } from "react";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import Contacts from "./pages/Contacts";
import Anonymous from "./pages/Anonymous";
import QuestionDetail from "./pages/QuestionDetail";
import BoxResponses from "./pages/BoxResponses";
import PublicQuestionForm from "./pages/PublicQuestionForm";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Navigation from "./components/shared/Navigation";
import AuthGuard from "./components/shared/AuthGuard";
import PasskeyPrompt, {
  shouldShowPasskeyPrompt,
} from "./components/shared/PasskeyPrompt";
import ReloadPrompt from "./components/shared/ReloadPrompt";
import OfflineBanner from "./components/shared/OfflineBanner";
import MediaExportBanner from "./components/shared/MediaExportBanner";
import AccountSelector from "./components/shared/AccountSelector";
import {
  extractAndSaveToken,
  extractAndSaveStatusId,
  getStoredToken,
  fetchUserStatuses,
} from "./utils/apiService";
import { useAppDispatch, useAppSelector } from "./hooks/redux";
import { fetchStatusMeAction, setStatusIdFromUrl, clearData } from "./store/dashboardSlice";
import type { UserStatusSummary } from "./types/dashboard";
import { AppContainer, MainContent } from "./App.styled";

function App() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const status = useAppSelector((state) => state.dashboard.status);

  const isPublicRoute = location.pathname.startsWith("/q/");
  const isContactsPage = location.pathname === "/contacts";
  const noBottomPadding = isPublicRoute || isContactsPage;
  const statusIdFromUrl = useAppSelector((state) => state.dashboard.statusIdFromUrl);
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [userStatuses, setUserStatuses] = useState<UserStatusSummary[]>([]);
  const [accountSelectorLoading, setAccountSelectorLoading] = useState(false);
  const [accountSelectorError, setAccountSelectorError] = useState<string | null>(null);

  const isLoginRoute = location.pathname === "/login";
  const isAuthCallback = location.pathname === "/auth/callback";
  const isUnauthRoute = isLoginRoute || isAuthCallback || isPublicRoute;

  const loadStatusMe = useCallback(() => {
    dispatch(fetchStatusMeAction())
      .unwrap()
      .then(() => {
        if (shouldShowPasskeyPrompt()) {
          setShowPasskeyPrompt(true);
        }
      })
      .catch(() => {
        navigate("/login", { replace: true });
      });
  }, [dispatch, navigate]);

  const loadUserStatuses = useCallback(async () => {
    setAccountSelectorLoading(true);
    setAccountSelectorError(null);
    try {
      const statuses = await fetchUserStatuses();
      if (statuses.length === 1) {
        dispatch(setStatusIdFromUrl(statuses[0].id));
        loadStatusMe();
      } else if (statuses.length > 1) {
        setUserStatuses(statuses);
        setShowAccountSelector(true);
      } else {
        loadStatusMe();
      }
    } catch (error) {
      const status = (error as { status?: number }).status;
      if (status === 401 || status === 403 || status === 404) {
        navigate("/login", { replace: true });
        return;
      }
      setAccountSelectorError("לא ניתן לטעון את רשימת החשבונות. נסה שוב.");
    } finally {
      setAccountSelectorLoading(false);
    }
  }, [dispatch, navigate, loadStatusMe]);

  const handleAccountSelect = useCallback((selectedStatusId: number) => {
    dispatch(clearData());
    dispatch(setStatusIdFromUrl(selectedStatusId));
    setShowAccountSelector(false);
    loadStatusMe();
  }, [dispatch, loadStatusMe]);

  useEffect(() => {
    if (isUnauthRoute) return;

    extractAndSaveToken();
    extractAndSaveStatusId();

    const token = getStoredToken();
    if (!token) return;

    if (!status) {
      if (statusIdFromUrl) {
        loadStatusMe();
      } else {
        loadUserStatuses();
      }
    }
  }, [dispatch, status, location.pathname, isUnauthRoute, statusIdFromUrl, loadStatusMe, loadUserStatuses]);

  useEffect(() => {
    if (isPublicRoute) return;

    extractAndSaveToken();
    extractAndSaveStatusId();
    if (!status) {
      dispatch(fetchStatusMeAction());
    }
  }, [dispatch, status, location.pathname, isPublicRoute]);

  return (
    <AppContainer>
      <MainContent
        $noBottomPadding={noBottomPadding} $isUnauthRoute={isUnauthRoute}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/"
            element={
              <AuthGuard>
                <Dashboard />
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard"
            element={
              <AuthGuard>
                <Dashboard />
              </AuthGuard>
            }
          />
          <Route
            path="/settings"
            element={
              <AuthGuard>
                <Settings />
              </AuthGuard>
            }
          />
          <Route
            path="/contacts"
            element={
              <AuthGuard>
                <Contacts />
              </AuthGuard>
            }
          />
          <Route 
            path="/anonymous"
            element={
              <AuthGuard>
                <Anonymous />
              </AuthGuard>
            }
          />
          <Route 
            path="/anonymous/:id/responses"
            element={
              <AuthGuard>
                <BoxResponses />
              </AuthGuard>
            }
          />
          <Route 
            path="/anonymous/:id"
            element={
              <AuthGuard>
                <QuestionDetail />
              </AuthGuard>
            }
          />
          <Route path="/q/:identifier" element={<PublicQuestionForm />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </MainContent>

      {!isUnauthRoute && <Navigation />}
      <PasskeyPrompt
        isVisible={showPasskeyPrompt}
        onDone={() => setShowPasskeyPrompt(false)}
      />
      <ReloadPrompt />
      <OfflineBanner />
      <MediaExportBanner />
      <AccountSelector
        isVisible={showAccountSelector || !!accountSelectorError}
        statuses={userStatuses}
        loading={accountSelectorLoading}
        error={accountSelectorError}
        onSelect={handleAccountSelect}
        onRetry={loadUserStatuses}
      />
    </AppContainer>
  );
}

export default App;
